import { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Context from "../../../../../Context";
import { serverLine } from "../../../../../controllers/network/serverLine";
import submitData from "../../../../../controllers/request/submitData";
import MaterialInput from "../../../../helperComponents/MaterialInput";
import OptionPicker from "../../../../helperComponents/OptionPicker";
import PrimaryButton from "../../../../helperComponents/PrimaryButton";
import ActivityTypePicker from "../ActivityTypePicker";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: clamp(12px, 2vw, 24px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  overflow: hidden;
`;

const SearchRow = styled.button`
  width: 100%;
  background: transparent;
  color: var(--color);
  border: none;
  border-bottom: 1px solid var(--border);
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--translucentLight);
  }
`;

const SearchInfo = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

export default function CreateManualActivityBookingForm({
  arrayItems,
  setArrayItems,
}) {
  const { setForm } = useContext(Context);
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchingUser, setSearchingUser] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const [formData, setFormData] = useState({
    userID: null,
    activityTypeID: null,
    participationCountType: "SOLO",
    date: null,
    hour: null,
    slot: null,
    status: "PENDING",
  });
  const [selectedTimeID, setSelectedTimeID] = useState(null);
  const [manualTime, setManualTime] = useState("");
  const [dateInput, setDateInput] = useState("");

  useEffect(() => {
    serverLine.get("/admin/activity-types").then((data) => {
      setActivityTypes(data || []);
    });
  }, []);

  useEffect(() => {
    let cleanedSearch = searchText.trim();
    if (cleanedSearch.length < 2) {
      setUserResults([]);
      setSearchingUser(false);
      return;
    }

    let isActive = true;
    let timeout = setTimeout(async () => {
      setSearchingUser(true);
      try {
        let output = await serverLine.get(
          `/admin/user-search?keyword=${encodeURIComponent(cleanedSearch)}`
        );
        if (isActive) setUserResults(output || []);
      } catch (e) {
        if (isActive) setUserResults([]);
      } finally {
        if (isActive) setSearchingUser(false);
      }
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  }, [searchText]);

  const selectedActivityType = useMemo(() => {
    return activityTypes.find((item) => item._id === formData.activityTypeID);
  }, [activityTypes, formData.activityTypeID]);

  const generatedTimeOptions = useMemo(() => {
    return getActivityTimeOptions(selectedActivityType);
  }, [selectedActivityType]);

  return (
    <Container>
      <MaterialInput
        value={searchText}
        label={t("adminActivityBookingFindUser")}
        placeholder={t("adminActivityBookingFindUserPlaceholder")}
        onChange={(e) => {
          setSearchText(e.target.value);
          setFormData({ ...formData, userID: null });
        }}
      />

      {getUserSearchOutput()}

      <ActivityTypePicker
        value={formData.activityTypeID}
        onChange={(value) => {
          setFormData({
            ...formData,
            activityTypeID: value,
            hour: null,
            slot: null,
          });
          setSelectedTimeID(null);
          setManualTime("");
        }}
        label={t("adminActivityBookingActivityType")}
      />

      <OptionPicker
        label={t("adminActivityBookingParticipationType")}
        value={formData.participationCountType}
        onChange={updateField("participationCountType")}
        options={[
          { label: t("adminActivityBookingParticipationSolo"), value: "SOLO" },
          { label: t("adminActivityBookingParticipationPair"), value: "PAIR" },
          { label: t("adminActivityBookingParticipationTrio"), value: "TRIO" },
        ]}
      />

      <MaterialInput
        value={dateInput}
        label={`${t("adminActivityBookingDate")} (JJ/MM/AAAA)`}
        placeholder="16/02/2026"
        onChange={(e) => {
          setDateInput(e.target.value);
        }}
      />

      {generatedTimeOptions.length ? (
        <OptionPicker
          label={t("adminActivityBookingTimeSlot")}
          value={selectedTimeID}
          onChange={(value) => {
            setSelectedTimeID(value);
            setManualTime("");
            let parsed = parseSelectedTimeValue(value);
            if (!parsed) return;
            setFormData({ ...formData, hour: parsed.hour, slot: parsed.slot });
          }}
          options={generatedTimeOptions}
        />
      ) : (
        <SearchInfo>{t("adminActivityBookingNoScheduleFound")}</SearchInfo>
      )}

      <MaterialInput
        value={manualTime}
        label={t("adminActivityBookingManualTime")}
        placeholder="08:30"
        onChange={(e) => {
          setManualTime(e.target.value);
          setSelectedTimeID(null);
          setFormData({ ...formData, hour: null, slot: null });
        }}
      />
      <SearchInfo>{t("adminActivityBookingManualTimeHint")}</SearchInfo>

      <OptionPicker
        label={t("adminActivityBookingStatus")}
        value={formData.status}
        onChange={updateField("status")}
        options={[
          { label: t("adminActivityBookingStatusPending"), value: "PENDING" },
          { label: t("adminActivityBookingStatusApproved"), value: "APPROVED" },
          { label: t("adminActivityBookingStatusDeclined"), value: "DECLINED" },
        ]}
      />

      <PrimaryButton onClick={onSubmit}>
        {submitting ? "..." : t("submit")}
      </PrimaryButton>
    </Container>
  );

  function getUserSearchOutput() {
    if (searchingUser)
      return <SearchInfo>{t("adminActivityBookingSearchInProgress")}</SearchInfo>;

    if (formData.userID) {
      return (
        <SearchInfo>
          {t("adminActivityBookingUserSelected")}: {searchText}
        </SearchInfo>
      );
    }

    if (searchText.trim().length < 2) return null;

    if (!userResults.length)
      return <SearchInfo>{t("adminActivityBookingNoUsersFound")}</SearchInfo>;

    return (
      <SearchResults>
        {userResults.map((user) => (
          <SearchRow
            key={user._id}
            onClick={() => {
              setFormData({ ...formData, userID: user._id });
              setSearchText(getUserLabel(user));
              setUserResults([]);
            }}
          >
            {getUserLabel(user)}
          </SearchRow>
        ))}
      </SearchResults>
    );
  }

  function getUserLabel(user) {
    let first = (user.firstName || "").trim();
    let last = (user.lastName || "").trim();
    let fullName = `${first} ${last}`.trim();
    if (!fullName) fullName = user.email || "User";
    return `${fullName} (${user.phoneNumber || user.email || "-"})`;
  }

  function updateField(fieldName) {
    return (value) => {
      setFormData({ ...formData, [fieldName]: value });
    };
  }

  function onSubmit() {
    if (!formData.userID)
      return window.popupAlert(t("adminActivityBookingPickUserFirst"));
    if (!formData.activityTypeID)
      return window.popupAlert(t("adminActivityBookingPickActivityFirst"));

    let parsedDate = parseFrenchDateInput(dateInput);
    if (!parsedDate)
      return window.popupAlert(t("adminActivityBookingInvalidFrenchDate"));

    let hour = formData.hour;
    let slot = formData.slot;

    if (manualTime.trim()) {
      let manualParsed = mapManualTimeToBookingSlot({
        manualTime,
        activityType: selectedActivityType,
      });
      if (!manualParsed)
        return window.popupAlert(t("adminActivityBookingManualTimeInvalid"));
      if (manualParsed.outOfSchedule)
        return window.popupAlert(t("adminActivityBookingManualTimeOutOfSchedule"));
      hour = manualParsed.hour;
      slot = manualParsed.slot;
    }

    if (hour === null || hour === undefined)
      return window.popupAlert(t("adminActivityBookingPickHourFirst"));
    if (!slot) return window.popupAlert(t("adminActivityBookingPickSlotFirst"));

    submitData({
      path: "/admin/activity-booking",
      method: "post",
      data: {
        ...formData,
        date: parsedDate,
        hour,
        slot,
      },
      setLoading: setSubmitting,
      onSuccess: (newItem) => {
        setForm(null);
        setArrayItems([newItem, ...(arrayItems || [])]);
      },
    });
  }
}

function getActivityTimeOptions(activityType) {
  if (!activityType) return [];

  let plannings = getAllPlannings(activityType);
  if (!plannings.length) return [];

  let divisions = parseInt(activityType.slotsPerHour);
  if (Number.isNaN(divisions) || divisions < 1) divisions = 1;
  let slotMins = Math.round(60 / divisions);
  if (slotMins < 1) slotMins = 60;

  let options = [];
  let seen = {};

  for (let planning of plannings) {
    let startMins = timeToMinutes(planning.startingTime);
    let endMins = timeToMinutes(planning.endingTime);
    let breakStartMins = timeToMinutes(planning.breakTimeStart);
    let breakEndMins = timeToMinutes(planning.breakTimeEnding);
    if (startMins === null || endMins === null || startMins >= endMins) continue;

    for (let mins = startMins; mins < endMins; mins += slotMins) {
      if (
        breakStartMins !== null &&
        breakEndMins !== null &&
        mins >= breakStartMins &&
        mins < breakEndMins
      ) {
        continue;
      }
      if (seen[mins]) continue;
      seen[mins] = true;

      let hour = Math.floor(mins / 60);
      let minsInHour = mins % 60;
      let slot = Math.floor(minsInHour / slotMins) + 1;
      if (slot < 1) slot = 1;
      if (slot > divisions) slot = divisions;

      options.push({
        value: `${mins}|${hour}|${slot}`,
        label: formatMinutes(mins),
      });
    }
  }

  return options.sort((a, b) => {
    return parseInt(a.value.split("|")[0]) - parseInt(b.value.split("|")[0]);
  });
}

function parseSelectedTimeValue(value) {
  if (!value || typeof value !== "string") return null;
  let parts = value.split("|");
  if (parts.length !== 3) return null;
  let hour = parseInt(parts[1]);
  let slot = parseInt(parts[2]);
  if (Number.isNaN(hour) || Number.isNaN(slot)) return null;
  return { hour, slot };
}

function mapManualTimeToBookingSlot({ manualTime, activityType }) {
  let mins = timeToMinutes(manualTime);
  if (mins === null) return null;

  let plannings = getAllPlannings(activityType);
  if (plannings.length && !isMinutesInsidePlannings(mins, plannings)) {
    return { outOfSchedule: true };
  }

  let divisions = parseInt(activityType?.slotsPerHour);
  if (Number.isNaN(divisions) || divisions < 1) divisions = 1;
  let slotMins = Math.round(60 / divisions);
  if (slotMins < 1) slotMins = 60;

  let hour = Math.floor(mins / 60);
  let minsInHour = mins % 60;
  let slot = Math.floor(minsInHour / slotMins) + 1;
  if (slot < 1) slot = 1;
  if (slot > divisions) slot = divisions;

  return { hour, slot, outOfSchedule: false };
}

function isMinutesInsidePlannings(mins, plannings) {
  for (let planning of plannings) {
    let startMins = timeToMinutes(planning.startingTime);
    let endMins = timeToMinutes(planning.endingTime);
    if (startMins === null || endMins === null || mins < startMins || mins >= endMins)
      continue;

    let breakStartMins = timeToMinutes(planning.breakTimeStart);
    let breakEndMins = timeToMinutes(planning.breakTimeEnding);
    if (
      breakStartMins !== null &&
      breakEndMins !== null &&
      mins >= breakStartMins &&
      mins < breakEndMins
    ) {
      continue;
    }

    return true;
  }
  return false;
}

function parseFrenchDateInput(text) {
  if (!text || typeof text !== "string") return null;
  let cleaned = text.trim();
  let match = cleaned.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!match) return null;

  let day = parseInt(match[1]);
  let month = parseInt(match[2]);
  let year = parseInt(match[3]);
  if ([day, month, year].some((v) => Number.isNaN(v))) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  let date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return `${year}-${month.toString().padStart(2, "0")}-${day
    .toString()
    .padStart(2, "0")}`;
}

function getAllPlannings(activityType) {
  if (!activityType) return [];
  if (Array.isArray(activityType.plannings) && activityType.plannings.length) {
    return activityType.plannings;
  }
  if (
    activityType.startingTime !== undefined ||
    activityType.endingTime !== undefined
  ) {
    return [
      {
        startingTime: activityType.startingTime,
        endingTime: activityType.endingTime,
        breakTimeStart: activityType.breakTimeStart,
        breakTimeEnding: activityType.breakTimeEnding,
      },
    ];
  }
  return [];
}

function timeToMinutes(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    if (Number.isNaN(value) || value < 0 || value > 23) return null;
    return Math.floor(value) * 60;
  }
  if (typeof value !== "string") return null;
  let text = value.trim().toLowerCase();
  if (!text.length) return null;

  let hhmm = text.match(/^(\d{1,2}):(\d{1,2})$/);
  if (hhmm) {
    let h = parseInt(hhmm[1]);
    let m = parseInt(hhmm[2]);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
  }

  let hourOnly = text.match(/^(\d{1,2})$/);
  if (hourOnly) {
    let h = parseInt(hourOnly[1]);
    if (Number.isNaN(h) || h < 0 || h > 23) return null;
    return h * 60;
  }

  let hFormat = text.match(/^(\d{1,2})h(\d{1,2})?$/);
  if (hFormat) {
    let h = parseInt(hFormat[1]);
    let m = hFormat[2] ? parseInt(hFormat[2]) : 0;
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
  }

  return null;
}

function formatMinutes(totalMins) {
  let hours = Math.floor(totalMins / 60);
  let mins = totalMins % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}
